/**
 * Date utility functions using Gregorian calendar (en-US locale)
 * All functions ensure solar-based (Gregorian) date formatting instead of Islamic-lunar (Hijri)
 */

/**
 * Format a date to full format with month name
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date (e.g., "January 15, 2024")
 */
export const formatDate = (date) => {
  if (!date) return '';
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return '';
  }
};

/**
 * Format a date to short format
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date (e.g., "Jan 15, 2024")
 */
export const formatShortDate = (date) => {
  if (!date) return '';
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return '';
  }
};

/**
 * Format time with AM/PM
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted time (e.g., "02:30 PM")
 */
export const formatTime = (date) => {
  if (!date) return '';
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return '';
  }
};

/**
 * Format combined date and time
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date and time (e.g., "January 15, 2024, 02:30 PM")
 */
export const formatDateTime = (date) => {
  if (!date) return '';
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return '';
  }
};

/**
 * Format date with weekday
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date with weekday (e.g., "Monday, January 15, 2024")
 */
export const formatDateWithWeekday = (date) => {
  if (!date) return '';
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return '';
  }
};

/**
 * Format relative time (time ago)
 * @param {Date|string} date - Date object or date string
 * @returns {string} Relative time string (e.g., "Just now", "5 minutes ago")
 */
export const formatTimeAgo = (dateString) => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    
    return formatShortDate(date);
  } catch {
    return '';
  }
};

